import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { usePacientes } from '../hooks/usePacientes';
import { useAuth } from '../contexts/AuthContext';
import { createPatient, updatePatient, deletePatient } from '../services/dataService';
import { get, ref } from '../services/phpDataAdapter';
import { database } from '../config/backend';
import { clearPatientDraft, emptyPatientForm, PatientFormData, readPatientDraft, savePatientDraft } from '../utils/patientDraft';
import './Pages.css';

function formatDataIso(iso: string) {
  if (!iso) return '-';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('pt-BR');
}

function mapStatusBadge(status: 'ativo' | 'avaliacao' | 'alerta') {
  if (status === 'ativo') {
    return { className: 'ns-badge ns-badge-success', label: 'Em acompanhamento' };
  }
  if (status === 'avaliacao') {
    return { className: 'ns-badge ns-badge-neutral', label: 'Em avaliação' };
  }
  return { className: 'ns-badge ns-badge-warning', label: 'Baixa adesão' };
}

export const Patients: React.FC = () => {
  const { user, createUserForNutritionist } = useAuth();
  const { pacientes, loading, refresh } = usePacientes();
  const [search, setSearch] = useState('');

  const [initialDraft] = useState(() => readPatientDraft(user?.uid));
  const [draftStorageError, setDraftStorageError] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(Boolean(initialDraft) || new URLSearchParams(window.location.search).get('novo') === '1');
  const [editingPatientId, setEditingPatientId] = useState<string | null>(initialDraft?.editingPatientId ?? null);
  const [deletingPatientId, setDeletingPatientId] = useState<string | null>(null);
  const [newPatient, setNewPatient] = useState<PatientFormData>(() => initialDraft?.data ?? emptyPatientForm());
  const [saving, setSaving] = useState(false);

  // Controle de senha (chave de login)
  const [manualPassword, setManualPassword] = useState(initialDraft?.manualPassword ?? false);
  const [customPassword, setCustomPassword] = useState('');
  const [customPasswordConfirm, setCustomPasswordConfirm] = useState('');

  const hasDraft = Boolean(editingPatientId || manualPassword || JSON.stringify(newPatient) !== JSON.stringify(emptyPatientForm()));

  useEffect(() => {
    if (!user?.uid || !isModalOpen) return;
    if (!hasDraft) { clearPatientDraft(user.uid); return; }
    setDraftStorageError(!savePatientDraft(user.uid, newPatient, editingPatientId, manualPassword));
  }, [user?.uid, isModalOpen, hasDraft, newPatient, editingPatientId, manualPassword]);

  useEffect(() => {
    if (!hasDraft || !user?.uid) return;
    const protectDraft = (event: BeforeUnloadEvent) => {
      savePatientDraft(user.uid, newPatient, editingPatientId, manualPassword);
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', protectDraft);
    return () => window.removeEventListener('beforeunload', protectDraft);
  }, [hasDraft, user?.uid, newPatient, editingPatientId, manualPassword]);

  const closeForm = () => { if (!saving) setIsModalOpen(false); };
  const discardForm = () => {
    if (saving || (hasDraft && !window.confirm('Descartar os dados não salvos deste paciente?'))) return;
    setIsModalOpen(false);
    resetForm();
  };

  const filtered = useMemo(() => {
    const term = search.toLowerCase().trim();
    if (!term) return pacientes;
    return pacientes.filter(
      (p) =>
        p.nome.toLowerCase().includes(term) ||
        p.email.toLowerCase().includes(term)
    );
  }, [pacientes, search]);

  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const random = new Uint8Array(16);
    window.crypto.getRandomValues(random);
    return Array.from(random, value => chars[value % chars.length]).join('');
  };

  const resetForm = () => {
    clearPatientDraft(user?.uid);
    setNewPatient(emptyPatientForm());
    setDraftStorageError(false);
    setManualPassword(false);
    setCustomPassword('');
    setCustomPasswordConfirm('');
    setEditingPatientId(null);
  };

  const handleSave = async () => {
    if (saving || !user || !formRef.current?.reportValidity() || !newPatient.nome.trim() || !newPatient.email.trim()) return;

    // Se estiver editando, não precisa validar senha
    if (editingPatientId) {
      setSaving(true);
      try {
        await updatePatient(user.uid, editingPatientId, newPatient);
        setIsModalOpen(false);
        resetForm();
        refresh();
      } catch (error: any) {
        let message = 'Erro ao atualizar paciente.';
        if (error?.message) {
          message = error.message;
        }
        alert(message);
        console.error(error);
      } finally {
        setSaving(false);
      }
      return;
    }

    // Validação de senha quando definida manualmente (apenas para criação)
    if (manualPassword) {
      if (!customPassword || !customPasswordConfirm) {
        alert('Informe e confirme a senha');
        return;
      }
      if (customPassword.length < 10) {
        alert('A senha deve ter pelo menos 10 caracteres');
        return;
      }
      if (customPassword !== customPasswordConfirm) {
        alert('As senhas não coincidem');
        return;
      }
    }

    setSaving(true);
    try {
      const password = manualPassword ? customPassword : generatePassword();
      await createPatient(user.uid, newPatient, password, createUserForNutritionist);
      setIsModalOpen(false);
      resetForm();
      refresh();
    } catch (error: any) {
      let message = 'Erro ao criar paciente.';
      if (error?.code === 'auth/email-already-in-use') {
        message = 'Este email já está sendo usado. Use um email diferente.';
      } else if (error?.code === 'auth/invalid-email') {
        message = 'Email inválido. Verifique o endereço informado.';
      } else if (error?.code === 'auth/weak-password') {
        message = 'Senha muito fraca. Gere novamente.';
      } else if (error?.message) {
        message = error.message;
      }
      alert(message);
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (patientId: string) => {
    if (!user || saving) return;
    if (hasDraft && !window.confirm('Abrir outro paciente e substituir o rascunho atual?')) return;

    try {
      // Buscar dados completos do paciente
      const [userSnap, patientSnap] = await Promise.all([
        get(ref(database, `users/${patientId}`)),
        get(ref(database, `patients/${patientId}`)),
      ]);

      if (!userSnap.exists()) {
        alert('Paciente não encontrado');
        return;
      }

      const userData = userSnap.val() as any;
      const patientData = patientSnap.exists() ? (patientSnap.val() as any) : {};

      const personalData = userData.personalData || {};

      setNewPatient({
        nome: userData.name || patientData.name || '',
        email: userData.email || '',
        cpf: personalData.cpf || '',
        rg: personalData.rg || '',
        birthDate: personalData.birthDate || '',
        gender: userData.gender || patientData.gender || 'male',
        bloodType: personalData.bloodType || '',
        phone: personalData.phone || '',
        address: personalData.address || '',
        city: personalData.city || '',
        state: personalData.state || '',
        zipCode: personalData.zipCode || '',
        height: userData.height ? String(userData.height) : '',
        weight: userData.weight ? String(userData.weight) : '',
        activityLevel: userData.activityLevel || patientData.activityLevel || 'moderate',
        goals: userData.goals || patientData.goals || [],
        category: patientData.status || userData.category || 'ativo',
        medicalConditions: userData.medicalConditions || patientData.medicalConditions || '',
        allergies: userData.allergies || patientData.allergies || '',
        medications: userData.medications || patientData.medications || '',
        supplements: userData.supplements || '',
        initialObservations: userData.initialObservations || '',
        objetivo: userData.goal || patientData.goal || (userData.goals && userData.goals.length > 0 ? userData.goals[0] : ''),
        gamification: userData.gamification || {
          enabled: false,
          sealThreshold: 70,
          specialRewardThreshold: 90,
          specialRewardText: '',
          unlockExerciseThreshold: 100,
        },
      });

      setEditingPatientId(patientId);
      setIsModalOpen(true);
    } catch (error: any) {
      alert('Erro ao carregar dados do paciente: ' + (error?.message || 'Erro desconhecido'));
      console.error(error);
    }
  };

  const handleDelete = async (patientId: string, patientName: string) => {
    if (!user) return;

    const confirmMessage = `Tem certeza que deseja excluir o paciente "${patientName}"?\n\nEsta ação não pode ser desfeita e removerá todos os dados do paciente.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setDeletingPatientId(patientId);
    try {
      await deletePatient(user.uid, patientId);
      alert('Paciente excluído com sucesso!');
      refresh();
    } catch (error: any) {
      let message = 'Erro ao excluir paciente.';
      if (error?.message) {
        message = error.message;
      }
      alert(message);
      console.error(error);
    } finally {
      setDeletingPatientId(null);
    }
  };

  return (
    <div className="ns-page">
      <div className="ns-page-header">
        <div>
          <h2>Pacientes</h2>
          <p>Gerencie sua base de pacientes e acesse detalhes rapidamente.</p>
        </div>
        <div className="ns-page-header-actions">
          <Button onClick={() => {
            setIsModalOpen(true);
          }}>{hasDraft ? 'Continuar rascunho' : 'Novo paciente'}</Button>
        </div>
      </div>

      <Card
        title="Lista de pacientes"
        subtitle="Dados reais sincronizados com o app mobile"
      >
        <div className="ns-table-toolbar">
          <input
            className="ns-input"
            placeholder="Buscar por nome ou email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="ns-table-wrapper">
          <table className="ns-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Email</th>
                <th>Objetivo</th>
                <th>Última consulta</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6}>Carregando pacientes...</td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={6}>Nenhum paciente encontrado.</td>
                </tr>
              )}
              {!loading &&
                filtered.map((p) => {
                  const { className, label } = mapStatusBadge(p.status);
                  const isDeleting = deletingPatientId === p.id;
                  return (
                    <tr key={p.id}>
                      <td>{p.nome}</td>
                      <td>{p.email}</td>
                      <td>{p.objetivo || '-'}</td>
                      <td>{formatDataIso(p.ultimaConsulta)}</td>
                      <td>
                        <span className={className}>{label}</span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <Button
                            variant="secondary"
                            onClick={() => handleEdit(p.id)}
                            style={{ padding: '4px 12px', fontSize: '13px' }}
                          >
                            Editar
                          </Button>
                          <Button
                            variant="secondary"
                            onClick={() => handleDelete(p.id, p.nome)}
                            disabled={isDeleting}
                            style={{
                              padding: '4px 12px',
                              fontSize: '13px',
                              background: 'var(--theme-danger-solid)',
                              color: 'white',
                              border: 'none'
                            }}
                          >
                            {isDeleting ? 'Excluindo...' : 'Excluir'}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </Card>

      <Modal
        isOpen={isModalOpen}
        onClose={closeForm}
        title={editingPatientId ? "Editar Paciente" : "Novo Paciente"}
        footer={
          <>
            <Button type="button" variant="secondary" onClick={discardForm} disabled={saving}>
              Descartar rascunho
            </Button>
            <Button type="button" onClick={handleSave} disabled={saving}>
              {saving ? 'Salvando...' : editingPatientId ? 'Atualizar' : 'Salvar'}
            </Button>
          </>
        }
      >
        <div style={{ maxHeight: '70vh', overflowY: 'auto', padding: '0 4px' }}>
          {initialDraft && <p role="status">Rascunho recuperado. Revise os dados e digite novamente a senha, se definida manualmente.</p>}
          {draftStorageError && <p role="alert">Não foi possível proteger o rascunho nesta aba. Mantenha esta página aberta até salvar.</p>}
          <form ref={formRef} className="ns-form" onSubmit={(e) => e.preventDefault()}>
            <h3 className="ns-section-title ns-section-title-first">
              Dados Pessoais
            </h3>
            <label className="ns-field">
              <span>Nome completo *</span>
              <input
                className="ns-input"
                value={newPatient.nome}
                onChange={(e) =>
                  setNewPatient({ ...newPatient, nome: e.target.value })
                }
                placeholder="Ex: Maria Silva"
                required
              />
            </label>
            <label className="ns-field">
              <span>Email *</span>
              <input
                className="ns-input"
                type="email"
                value={newPatient.email}
                onChange={(e) =>
                  setNewPatient({ ...newPatient, email: e.target.value })
                }
                placeholder="Ex: maria@email.com"
                required
              />
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <label className="ns-field">
                <span>CPF</span>
                <input
                  className="ns-input"
                  value={newPatient.cpf}
                  onChange={(e) =>
                    setNewPatient({ ...newPatient, cpf: e.target.value })
                  }
                  placeholder="000.000.000-00"
                />
              </label>
              <label className="ns-field">
                <span>RG</span>
                <input
                  className="ns-input"
                  value={newPatient.rg}
                  onChange={(e) =>
                    setNewPatient({ ...newPatient, rg: e.target.value })
                  }
                  placeholder="00.000.000-0"
                />
              </label>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <label className="ns-field">
                <span>Data de Nascimento</span>
                <input
                  className="ns-input"
                  type="date"
                  value={newPatient.birthDate}
                  onChange={(e) =>
                    setNewPatient({ ...newPatient, birthDate: e.target.value })
                  }
                />
              </label>
              <label className="ns-field">
                <span>Gênero</span>
                <select
                  className="ns-input"
                  value={newPatient.gender}
                  onChange={(e) =>
                    setNewPatient({ ...newPatient, gender: e.target.value as 'male' | 'female' })
                  }
                >
                  <option value="male">Masculino</option>
                  <option value="female">Feminino</option>
                </select>
              </label>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <label className="ns-field">
                <span>Telefone</span>
                <input
                  className="ns-input"
                  type="tel"
                  value={newPatient.phone}
                  onChange={(e) =>
                    setNewPatient({ ...newPatient, phone: e.target.value })
                  }
                  placeholder="(00) 00000-0000"
                />
              </label>
              <label className="ns-field">
                <span>Tipo Sanguíneo</span>
                <input
                  className="ns-input"
                  value={newPatient.bloodType}
                  onChange={(e) =>
                    setNewPatient({ ...newPatient, bloodType: e.target.value })
                  }
                  placeholder="Ex: A+, O-"
                />
              </label>
            </div>

            <h3 className="ns-section-title">
              Endereço
            </h3>
            <label className="ns-field">
              <span>Endereço</span>
              <input
                className="ns-input"
                value={newPatient.address}
                onChange={(e) =>
                  setNewPatient({ ...newPatient, address: e.target.value })
                }
                placeholder="Rua, número, complemento"
              />
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '12px' }}>
              <label className="ns-field">
                <span>Cidade</span>
                <input
                  className="ns-input"
                  value={newPatient.city}
                  onChange={(e) =>
                    setNewPatient({ ...newPatient, city: e.target.value })
                  }
                  placeholder="Cidade"
                />
              </label>
              <label className="ns-field">
                <span>Estado</span>
                <input
                  className="ns-input"
                  value={newPatient.state}
                  onChange={(e) =>
                    setNewPatient({ ...newPatient, state: e.target.value })
                  }
                  placeholder="UF"
                  maxLength={2}
                />
              </label>
              <label className="ns-field">
                <span>CEP</span>
                <input
                  className="ns-input"
                  value={newPatient.zipCode}
                  onChange={(e) =>
                    setNewPatient({ ...newPatient, zipCode: e.target.value })
                  }
                  placeholder="00000-000"
                />
              </label>
            </div>

            {!editingPatientId && (
              <>
                <h3 className="ns-section-title">
                  Senha de Acesso
                </h3>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', padding: '8px 0' }}>
                  <span style={{ fontSize: '14px' }}>Definir senha manualmente</span>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={manualPassword}
                      onChange={(e) => setManualPassword(e.target.checked)}
                      style={{ marginRight: '8px', cursor: 'pointer' }}
                    />
                  </label>
                </div>

                {manualPassword && (
                  <>
                    <label className="ns-field">
                      <span>Senha do Cliente</span>
                      <input
                        className="ns-input"
                        type="password"
                        value={customPassword}
                        onChange={(e) => setCustomPassword(e.target.value)}
                        placeholder="Mínimo 6 caracteres"
                      />
                    </label>
                    <label className="ns-field">
                      <span>Confirmar Senha</span>
                      <input
                        className="ns-input"
                        type="password"
                        value={customPasswordConfirm}
                        onChange={(e) => setCustomPasswordConfirm(e.target.value)}
                        placeholder="Digite a senha novamente"
                      />
                    </label>
                  </>
                )}
              </>
            )}

            <h3 className="ns-section-title">
              Dados Físicos
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <label className="ns-field">
                <span>Altura (cm)</span>
                <input
                  className="ns-input"
                  type="number"
                  value={newPatient.height}
                  onChange={(e) =>
                    setNewPatient({ ...newPatient, height: e.target.value })
                  }
                  placeholder="170"
                />
              </label>
              <label className="ns-field">
                <span>Peso (kg)</span>
                <input
                  className="ns-input"
                  type="number"
                  value={newPatient.weight}
                  onChange={(e) =>
                    setNewPatient({ ...newPatient, weight: e.target.value })
                  }
                  placeholder="70"
                />
              </label>
            </div>
            <label className="ns-field">
              <span>Nível de Atividade</span>
              <select
                className="ns-input"
                value={newPatient.activityLevel}
                onChange={(e) =>
                  setNewPatient({ ...newPatient, activityLevel: e.target.value as any })
                }
              >
                <option value="sedentary">Sedentário</option>
                <option value="light">Leve</option>
                <option value="moderate">Moderado</option>
                <option value="active">Ativo</option>
                <option value="very_active">Muito Ativo</option>
              </select>
            </label>

            <h3 className="ns-section-title">
              Objetivos
            </h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
              {[
                { value: 'weight_loss', label: 'Emagrecimento' },
                { value: 'muscle_gain', label: 'Ganho de Massa Muscular' },
                { value: 'health', label: 'Saúde Geral' },
                { value: 'performance', label: 'Performance Esportiva' },
                { value: 'body_composition', label: 'Composição Corporal' },
                { value: 'maintenance', label: 'Manutenção' },
              ].map((goal) => (
                <label
                  key={goal.value}
                  className={`ns-goal-chip${newPatient.goals.includes(goal.value) ? ' is-selected' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={newPatient.goals.includes(goal.value)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setNewPatient({
                          ...newPatient,
                          goals: [...newPatient.goals, goal.value],
                          objetivo: goal.value, // Mantém compatibilidade
                        });
                      } else {
                        setNewPatient({
                          ...newPatient,
                          goals: newPatient.goals.filter((g) => g !== goal.value),
                          objetivo: newPatient.goals.length > 1 ? newPatient.goals[0] : '',
                        });
                      }
                    }}
                  />
                  <span className="ns-goal-chip-label">{goal.label}</span>
                </label>
              ))}
            </div>

            <label className="ns-field">
              <span>Categoria</span>
              <select
                className="ns-input"
                value={newPatient.category}
                onChange={(e) =>
                  setNewPatient({ ...newPatient, category: e.target.value as any })
                }
              >
                <option value="ativo">Ativo</option>
                <option value="avaliacao">Avaliação</option>
                <option value="alerta">Alerta</option>
              </select>
            </label>

            <h3 className="ns-section-title">
              Informações Médicas
            </h3>
            <label className="ns-field">
              <span>Condições Médicas</span>
              <textarea
                className="ns-input"
                value={newPatient.medicalConditions}
                onChange={(e) =>
                  setNewPatient({ ...newPatient, medicalConditions: e.target.value })
                }
                placeholder="Ex: Hipertensão, diabetes..."
                rows={3}
              />
            </label>
            <label className="ns-field">
              <span>Alergias</span>
              <textarea
                className="ns-input"
                value={newPatient.allergies}
                onChange={(e) =>
                  setNewPatient({ ...newPatient, allergies: e.target.value })
                }
                placeholder="Ex: Alergia a frutos do mar, amendoim..."
                rows={2}
              />
            </label>
            <label className="ns-field">
              <span>Medicamentos</span>
              <textarea
                className="ns-input"
                value={newPatient.medications}
                onChange={(e) =>
                  setNewPatient({ ...newPatient, medications: e.target.value })
                }
                placeholder="Ex: Losartana 50mg 1x/dia..."
                rows={2}
              />
            </label>
            <label className="ns-field">
              <span>Suplementos</span>
              <textarea
                className="ns-input"
                value={newPatient.supplements}
                onChange={(e) =>
                  setNewPatient({ ...newPatient, supplements: e.target.value })
                }
                placeholder="Ex: Whey protein, vitamina D..."
                rows={2}
              />
            </label>

            <h3 className="ns-section-title">
              Observações Iniciais
            </h3>
            <label className="ns-field">
              <span>Observações sobre o paciente</span>
              <textarea
                className="ns-input"
                value={newPatient.initialObservations}
                onChange={(e) =>
                  setNewPatient({ ...newPatient, initialObservations: e.target.value })
                }
                placeholder="Anote informações importantes sobre o paciente..."
                rows={4}
              />
            </label>

            <h3 className="ns-section-title">
              Gamificação
            </h3>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={newPatient.gamification.enabled}
                onChange={(e) =>
                  setNewPatient({
                    ...newPatient,
                    gamification: { ...newPatient.gamification, enabled: e.target.checked }
                  })
                }
              />
              <span style={{ fontSize: '14px' }}>Ativar Gamificação</span>
            </label>

            {newPatient.gamification.enabled && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <label className="ns-field">
                    <span>Meta para Selo (%)</span>
                    <input
                      className="ns-input"
                      type="number"
                      value={newPatient.gamification.sealThreshold}
                      onChange={(e) =>
                        setNewPatient({
                          ...newPatient,
                          gamification: { ...newPatient.gamification, sealThreshold: Number(e.target.value) }
                        })
                      }
                    />
                  </label>
                  <label className="ns-field">
                    <span>Meta Recompensa (%)</span>
                    <input
                      className="ns-input"
                      type="number"
                      value={newPatient.gamification.specialRewardThreshold}
                      onChange={(e) =>
                        setNewPatient({
                          ...newPatient,
                          gamification: { ...newPatient.gamification, specialRewardThreshold: Number(e.target.value) }
                        })
                      }
                    />
                  </label>
                </div>
                <label className="ns-field">
                  <span>Texto da Recompensa Especial</span>
                  <input
                    className="ns-input"
                    value={newPatient.gamification.specialRewardText}
                    onChange={(e) =>
                      setNewPatient({
                        ...newPatient,
                        gamification: { ...newPatient.gamification, specialRewardText: e.target.value }
                      })
                    }
                    placeholder="Ex: Consulta grátis, desconto..."
                  />
                </label>
                <label className="ns-field">
                  <span>Meta Desbloqueio Exercícios (%)</span>
                  <input
                    className="ns-input"
                    type="number"
                    value={newPatient.gamification.unlockExerciseThreshold}
                    onChange={(e) =>
                      setNewPatient({
                        ...newPatient,
                        gamification: { ...newPatient.gamification, unlockExerciseThreshold: Number(e.target.value) }
                      })
                    }
                  />
                </label>
              </>
            )}
          </form>
        </div>
      </Modal>
    </div>
  );
};

